// Compiles + runs an untrusted one-shot data/transformation script with
// @babel/standalone, evaluating it inside a controlled scope that exposes
// the SAME live-data access the dashboard React code gets — only as async
// functions (`fetchTagValues`, `fetchTagHistory`, …) instead of React hooks,
// since a script has no React render cycle.
//
// This is the script-side analogue of `render-runtime.ts` (which compiles +
// renders dashboard TSX). It backs the dashboard-builder's
// `run_dashboard_script` tool: the agent writes a script that fetches data,
// transforms it, and `return`s the result, so it can validate a
// transformation BEFORE baking it into a dashboard. Because the script
// fetches through the exact same `data-runtime` plumbing the hooks use, a
// transformation that works here behaves identically once wired into TSX.

import * as ReactNamespace from 'react'

import {
  fetchActiveAlarms,
  fetchAlarmStatus,
  fetchCypherQuery,
  fetchDbConnections,
  fetchAgentFindings,
  fetchAgentResults,
  fetchDbQuery,
  fetchGatewayStatus,
  fetchPowerAnalysis,
  fetchSoapOperation,
  fetchTagHistory,
  fetchTagValues,
  writeDbValue,
  writeTagValue
} from './data-runtime'

/** Max size of the JSON-serialized result before it is truncated. */
const MAX_RESULT_BYTES = 16_000
/** Max number of captured console lines. */
const MAX_LOG_LINES = 100
/** Max length of a single captured console line. */
const MAX_LOG_LINE_LENGTH = 2_000

export interface ScriptRunResult {
  /** false when the script threw or failed to compile. */
  ok: boolean
  /**
   * JSON of the script's resolved return value, capped at
   * {@link MAX_RESULT_BYTES}. When `truncated` is true this is a best-effort
   * truncated string (no longer valid JSON). `null` when the script returned
   * `undefined` or threw before returning.
   */
  resultJson: string | null
  truncated: boolean
  /** Captured `console.log`/`info`/`warn`/`error` output, in order. */
  logs: string[]
  error: string | null
}

// Everything a script can reference without importing. Mirrors the hook set
// the dashboard code has as async functions. `writeTagValue` is the sole
// mutation and must be invoked only for an explicit operator-requested write.
// Keep this aligned with `data-runtime.ts` and the dashboard authoring docs.
export const SCRIPT_DATA_FNS = {
  fetchTagValues,
  fetchTagHistory,
  fetchActiveAlarms,
  fetchAlarmStatus,
  fetchCypherQuery,
  fetchGatewayStatus,
  fetchDbConnections,
  fetchDbQuery,
  fetchPowerAnalysis,
  fetchSoapOperation,
  fetchAgentFindings,
  fetchAgentResults,
  writeDbValue,
  writeTagValue
} as const

const SCRIPT_DATA_MODULE = { ...SCRIPT_DATA_FNS, __esModule: true }

type BabelModule = typeof import('@babel/standalone')

function formatLogArg(value: unknown): string {
  if (typeof value === 'string') return value
  try {
    return JSON.stringify(value)
  } catch {
    return String(value)
  }
}

/** A console-shaped object that buffers output (the script's `console`). */
function makeCaptureConsole(
  logs: string[]
): Pick<Console, 'log' | 'info' | 'warn' | 'error' | 'debug'> {
  const push = (level: string, args: unknown[]): void => {
    if (logs.length >= MAX_LOG_LINES) return
    const line = `[${level}] ${args.map(formatLogArg).join(' ')}`
    logs.push(line.length > MAX_LOG_LINE_LENGTH ? `${line.slice(0, MAX_LOG_LINE_LENGTH)}…` : line)
  }
  return {
    log: (...args: unknown[]) => push('log', args),
    info: (...args: unknown[]) => push('info', args),
    warn: (...args: unknown[]) => push('warn', args),
    error: (...args: unknown[]) => push('error', args),
    debug: (...args: unknown[]) => push('debug', args)
  }
}

/** Serialize `value` to JSON, truncating (and flagging) past the size cap. */
function serializeResult(value: unknown): { resultJson: string | null; truncated: boolean } {
  if (value === undefined) return { resultJson: null, truncated: false }
  let json: string
  try {
    json = JSON.stringify(value) ?? 'null'
  } catch (err) {
    // Circular / non-serializable result — fall back to a string preview.
    json = `"[unserializable result: ${err instanceof Error ? err.message : String(err)}]"`
  }
  if (json.length > MAX_RESULT_BYTES) {
    return { resultJson: `${json.slice(0, MAX_RESULT_BYTES)}…`, truncated: true }
  }
  return { resultJson: json, truncated: false }
}

/**
 * Compile + run `code` and resolve to a structured {@link ScriptRunResult}.
 * The script must default-export an async function (its resolved value is
 * the result) — or a plain value, which is used directly. Never throws: all
 * failures land in `ok: false` + `error`.
 */
export async function runScript(babel: BabelModule, code: string): Promise<ScriptRunResult> {
  const logs: string[] = []
  try {
    const transformed = babel.transform(code, {
      filename: 'script.tsx',
      presets: [['react'], ['typescript', { isTSX: true, allExtensions: true }]],
      plugins: ['transform-modules-commonjs']
    }).code

    if (transformed === null || transformed === undefined) {
      return {
        ok: false,
        resultJson: null,
        truncated: false,
        logs,
        error: 'Empty compiled output.'
      }
    }

    const requireShim = (name: string): unknown => {
      if (name === 'react') return ReactNamespace
      // Any other import (e.g. a relative './data-runtime' or '@/data') resolves
      // to the data-function module so destructured imports still work; bare
      // references resolve via the injected scope params below.
      return SCRIPT_DATA_MODULE
    }

    const captureConsole = makeCaptureConsole(logs)
    const moduleObj: { exports: Record<string, unknown> } = { exports: {} }
    const scopeNames = [...Object.keys(SCRIPT_DATA_FNS), 'console']
    const scopeValues = [...Object.values(SCRIPT_DATA_FNS), captureConsole]

    // eslint-disable-next-line @typescript-eslint/no-implied-eval, no-new-func
    const factory = new Function('require', 'exports', 'module', ...scopeNames, transformed)
    factory(requireShim, moduleObj.exports, moduleObj, ...scopeValues)

    const exported = moduleObj.exports as { default?: unknown }
    const candidate = exported.default ?? moduleObj.exports
    const value = typeof candidate === 'function' ? await candidate() : candidate

    const { resultJson, truncated } = serializeResult(value)
    return { ok: true, resultJson, truncated, logs, error: null }
  } catch (err) {
    return {
      ok: false,
      resultJson: null,
      truncated: false,
      logs,
      error: err instanceof Error ? err.message : String(err)
    }
  }
}
